import { useMutation, useQueryClient } from '@tanstack/react-query';
import { DcRuntimeApi } from '@api/bff/openapi/endpoints/dc-runtime-api';
import type {
  CreateDcRuntimeDto,
  DcRuntimeViewModel
} from '@api/bff/openapi/models';
import { bffApiClient } from '@api/bff/client';
import { dcRuntimeKeys, DC_RUNTIME_QUERY_KEY } from '../constants';
import { useApiToast } from '@hooks/useApiToast';
import { db } from '@db/database';
import { enqueue } from '@services/offlineSyncService';
import { useUserStore } from '@stores/userStore';

type CreateDcRuntimeContext = {
  woDetailGlobalId?: string | null;
};

export const useCreateDcRuntime = () => {
  const queryClient = useQueryClient();
  const { showError } = useApiToast();
  const dcRuntimeApi = new DcRuntimeApi(undefined, undefined, bffApiClient);
  const userName = useUserStore(state => state.userName) ?? '';

  return useMutation<
    DcRuntimeViewModel,
    Error,
    CreateDcRuntimeDto,
    CreateDcRuntimeContext
  >({
    networkMode: 'always',
    mutationFn: async (dto: CreateDcRuntimeDto) => {
      if (!navigator.onLine) {
        const tempId = `temp-${crypto.randomUUID()}`;
        const now = new Date().toISOString();

        const localRuntime: DcRuntimeViewModel = {
          id: tempId,
          gagesGlobalId: dto.gagesGlobalId,
          moduleType: dto.moduleType,
          woDetailGlobalId: dto.woDetailGlobalId,
          lastModified: now,
          lastModifiedBy: dto.lastModifiedBy,
          eventNum: dto.eventNum ?? null,
          jsonData: dto.jsonData ?? null
        };

        await db.cachedCalibrationRuntimes.add({
          ...localRuntime,
          cachedAt: now,
          _userId: userName
        });

        await enqueue({
          entityType: 'dc_runtime_create',
          entityKey: `${dto.woDetailGlobalId ?? 'unknown'}:${tempId}`,
          payload: {
            createDcRuntimeDto: dto,
            tempId
          },
          priority: 5
        });

        return localRuntime;
      }

      const response = await dcRuntimeApi.apiDcRuntimePost({
        createDcRuntimeDto: dto
      });
      return response.data;
    },

    onMutate: async variables => {
      return {
        woDetailGlobalId: variables.woDetailGlobalId
      };
    },

    onSuccess: (data, _variables, context) => {
      if (context?.woDetailGlobalId) {
        queryClient.invalidateQueries({
          queryKey: dcRuntimeKeys.byWoDetailGlobalId(context.woDetailGlobalId)
        });

        queryClient.setQueryData(
          dcRuntimeKeys.byWoDetailGlobalId(context.woDetailGlobalId),
          data
        );
      }

      if (data.id) {
        queryClient.setQueryData(dcRuntimeKeys.byId(data.id), data);
      }

      queryClient.invalidateQueries({
        queryKey: [DC_RUNTIME_QUERY_KEY]
      });
    },

    onError: error => {
      showError('calibration.create.error', error, 'calibrationApi');
    }
  });
};
